import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  RefreshCw, 
  Star, 
  Camera, 
  Trash2, 
  RotateCcw,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Phone,
  Globe,
  Clock,
  Navigation,
  Building
} from 'lucide-react';
import { placesAPI, campusAPI } from '../api';
import { Place, PlacesFilters, Campus } from '../types';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNotification } from '../hooks/useNotification';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

export const PlacesPage: React.FC = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCampus, setSelectedCampus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hierarchyView, setHierarchyView] = useState<'all' | 'top-level'>('top-level');
  const [expandedPlaces, setExpandedPlaces] = useState<Set<string>>(new Set());
  const [nestedPlacesMap, setNestedPlacesMap] = useState<Map<string, Place[]>>(new Map());
  const [loadingNested, setLoadingNested] = useState<Set<string>>(new Set());
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<PlacesFilters>({
    page: 1,
    limit: 20,
    is_deleted: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showNotification } = useNotification();

  // Fetch campuses
  const fetchCampuses = async () => {
    try {
      const data = await campusAPI.getAllCampuses();
      // Handle both array and object response
      const campusList = Array.isArray(data) ? data : (data?.data || []);
      setCampuses(campusList);
    } catch (err) {
      console.error('Failed to fetch campuses:', err);
      setCampuses([]); // Ensure campuses is always an array
    }
  };

  // Fetch places
  const fetchPlaces = async () => {
    if (!selectedCampus) return;
    
    try {
      setLoading(true);
      const response = await placesAPI.getAllPlaces({
        ...filters,
        campus_id: selectedCampus,
        hierarchy_level: hierarchyView === 'top-level' ? 0 : undefined,
        search: searchTerm || undefined,
      });
      
      setPlaces(response.places);
      setCurrentPage(response.page);
      setTotalPages(response.pages);
    } catch (err) {
      console.error('Error fetching places:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch nested places for a specific place
  const fetchNestedPlaces = async (placeId: string) => {
    if (loadingNested.has(placeId)) return;
    
    setLoadingNested(prev => new Set(prev).add(placeId));
    
    try {
      const response = await placesAPI.getNestedPlaces(placeId);
      setNestedPlacesMap(prev => new Map(prev).set(placeId, response.nested_places));
    } catch (err) {
      console.error('Failed to fetch nested places:', err);
      showNotification('Failed to load nested places', 'error');
    } finally {
      setLoadingNested(prev => {
        const newSet = new Set(prev);
        newSet.delete(placeId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchCampuses();
  }, []);

  useEffect(() => {
    if (selectedCampus) {
      setFilters(prev => ({
        ...prev,
        campus_id: selectedCampus,
        page: 1,
      }));
    }
  }, [selectedCampus]);

  useEffect(() => {
    if (selectedCampus) {
      fetchPlaces();
    }
  }, [filters, selectedCampus, hierarchyView, searchTerm]);

  // Toggle expanded place
  const toggleExpanded = async (placeId: string) => {
    const newExpanded = new Set(expandedPlaces);
    if (newExpanded.has(placeId)) {
      newExpanded.delete(placeId);
    } else {
      newExpanded.add(placeId);
      // Fetch nested places if not already loaded
      if (!nestedPlacesMap.has(placeId)) {
        await fetchNestedPlaces(placeId);
      }
    }
    setExpandedPlaces(newExpanded);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchPlaces();
    showNotification('Refreshing places...', 'info');
  };

  // Handle sync photos
  const handleSyncPhotos = async (placeId: string) => {
    try {
      setIsSubmitting(true);
      await placesAPI.syncPhotos(placeId);
      showNotification('Photos synced successfully', 'success');
      fetchPlaces();
    } catch {
      showNotification('Failed to sync photos', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete place
  const handleDeletePlace = async (placeId: string) => {
    try {
      setIsSubmitting(true);
      await placesAPI.deletePlace(placeId);
      showNotification('Place deleted successfully', 'success');
      fetchPlaces();
    } catch {
      showNotification('Failed to delete place', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle restore place
  const handleRestorePlace = async (placeId: string) => {
    try {
      setIsSubmitting(true);
      await placesAPI.restorePlace(placeId);
      showNotification('Place restored successfully', 'success');
      fetchPlaces();
    } catch {
      showNotification('Failed to restore place', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open place details
  const openPlaceDetails = (place: Place) => {
    setSelectedPlace(place);
    setIsDetailsOpen(true);
  };

  // Render place row
  const renderPlaceRow = (place: Place, level: number = 0) => {
    const isExpanded = expandedPlaces.has(place.place_id);
    const hasNested = place.has_nested_places || (place.nested_places_count !== undefined && place.nested_places_count > 0);
    const nestedPlaces = nestedPlacesMap.get(place.place_id) || [];
    const isLoadingNested = loadingNested.has(place.place_id);

    return (
      <React.Fragment key={place.place_id}>
        <TableRow className={`hover:bg-gray-50 ${level > 0 ? 'bg-gray-50/50' : ''}`}>
          <TableCell>
            {place.photo_info?.thumbnails?.[0]?.thumb_url || place.photos?.[0]?.optimized_urls?.thumb || place.photos?.[0]?.photo_url ? (
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={place.photo_info?.thumbnails?.[0]?.thumb_url || place.photos?.[0]?.optimized_urls?.thumb || place.photos?.[0]?.photo_url}
                  alt={place.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                    }
                  }}
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                <Camera className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 24}px` }}>
              {hasNested && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpanded(place.place_id)}
                  className="p-0 h-6 w-6"
                >
                  {isLoadingNested ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}
              {!hasNested && level > 0 && <div className="w-6" />}
              <div className="flex-1">
                <button
                  onClick={() => openPlaceDetails(place)}
                  className="text-left hover:text-blue-600 font-medium"
                >
                  {place.name}
                </button>
                {place.place_category && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {place.place_category}
                  </Badge>
                )}
              </div>
            </div>
          </TableCell>
          <TableCell className="text-sm text-gray-600">
            {place.formatted_address || place.address || '-'}
          </TableCell>
          <TableCell>
            {place.google_types?.slice(0, 2).map((type) => (
              <Badge key={type} variant="secondary" className="mr-1 text-xs">
                {type.replace(/_/g, ' ')}
              </Badge>
            ))}
            {place.google_types && place.google_types.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{place.google_types.length - 2}
              </Badge>
            )}
          </TableCell>
          <TableCell>
            <div className="text-sm">
              Level {place.hierarchy_level || 0}
              {place.nested_places_count !== undefined && place.nested_places_count > 0 && (
                <span className="text-gray-500 ml-1">
                  ({place.nested_places_count} nested)
                </span>
              )}
            </div>
          </TableCell>
          <TableCell>
            {place.rating && (
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 text-yellow-500 fill-current" />
                <span className="text-sm">{place.rating.toFixed(1)}</span>
                {place.user_ratings_total && (
                  <span className="text-xs text-gray-500">
                    ({place.user_ratings_total})
                  </span>
                )}
              </div>
            )}
          </TableCell>
          <TableCell>
            {place.photo_info ? (
              <div className="flex items-center gap-1">
                <Camera className="h-4 w-4 text-gray-500" />
                <span className="text-sm">
                  {place.photo_info.synced_photos}/{place.photo_info.total_photos}
                </span>
              </div>
            ) : (
              '-'
            )}
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              {place.is_synced ? (
                <Badge variant="default" className="text-xs">Synced</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">Not Synced</Badge>
              )}
              {place.is_deleted && (
                <Badge variant="destructive" className="text-xs">Deleted</Badge>
              )}
            </div>
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSyncPhotos(place.place_id)}
                disabled={isSubmitting}
                title="Sync Photos"
              >
                <Camera className="h-4 w-4" />
              </Button>
              {place.is_deleted ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRestorePlace(place.place_id)}
                  disabled={isSubmitting}
                  title="Restore"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeletePlace(place.place_id)}
                  disabled={isSubmitting}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
        {isExpanded && nestedPlaces.map((nestedPlace) => 
          renderPlaceRow(nestedPlace, level + 1)
        )}
      </React.Fragment>
    );
  };

  // Don't show loading state before campus selection - it will be handled inside the layout
  // if (loading && !selectedCampus) return <LoadingState />;
  // if (error && !selectedCampus) return <ErrorState message={error} />;

  return (
    <DashboardLayout>
      <PageHeader
        title="Places Management"
        description="Manage places across different campuses"
      />

      <div className="space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="campus">Campus</Label>
                <Select value={selectedCampus} onValueChange={setSelectedCampus}>
                  <SelectTrigger id="campus">
                    <SelectValue placeholder="Select a campus..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(campuses) && campuses.length > 0 ? (
                      campuses.map((campus) => (
                        <SelectItem key={campus._id} value={campus._id}>
                          {campus.campus_name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No campuses available
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="hierarchy">View</Label>
                <Select 
                  value={hierarchyView} 
                  onValueChange={(value) => setHierarchyView(value as 'all' | 'top-level')}
                  disabled={!selectedCampus}
                >
                  <SelectTrigger id="hierarchy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-level">Top Level Only</SelectItem>
                    <SelectItem value="all">All Places</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search places..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={!selectedCampus}
                />
              </div>
              
              <div className="flex items-end">
                <Button
                  onClick={handleRefresh}
                  disabled={!selectedCampus || loading}
                  className="w-full"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Places Table */}
        {!selectedCampus ? (
          <Card>
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Select a campus to view places</h3>
              <p className="text-gray-500">
                Choose a campus from the dropdown above to see all places associated with it.
              </p>
            </CardContent>
          </Card>
        ) : loading ? (
          <LoadingState />
        ) : places.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No places found</h3>
              <p className="text-gray-500">
                There are no places associated with this campus.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Photo</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Hierarchy</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Photos</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {places.map((place) => renderPlaceRow(place))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setFilters(prev => ({ ...prev, page: currentPage - 1 }))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = currentPage - 2 + i;
                    if (page < 1 || page > totalPages) return null;
                    return (
                      <Button
                        key={page}
                        variant={page === currentPage ? "default" : "outline"}
                        onClick={() => setFilters(prev => ({ ...prev, page }))}
                      >
                        {page}
                      </Button>
                    );
                  }).filter(Boolean)}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setFilters(prev => ({ ...prev, page: currentPage + 1 }))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Place Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPlace?.name}</DialogTitle>
            <DialogDescription>
              Place details and information
            </DialogDescription>
          </DialogHeader>
          {selectedPlace && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold mb-2">Basic Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Place ID:</span> {selectedPlace.place_id}
                  </div>
                  <div>
                    <span className="text-gray-500">Category:</span> {selectedPlace.place_category || 'N/A'}
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Address:</span> {selectedPlace.formatted_address || selectedPlace.address || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              {(selectedPlace.formatted_phone_number || selectedPlace.website) && (
                <div>
                  <h4 className="font-semibold mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    {selectedPlace.formatted_phone_number && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-500" />
                        <span>{selectedPlace.formatted_phone_number}</span>
                      </div>
                    )}
                    {selectedPlace.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-gray-500" />
                        <a href={selectedPlace.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {selectedPlace.website}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location */}
              {selectedPlace.geometry?.location && (
                <div>
                  <h4 className="font-semibold mb-2">Location</h4>
                  <div className="flex items-center gap-2 text-sm">
                    <Navigation className="h-4 w-4 text-gray-500" />
                    <span>
                      {selectedPlace.geometry.location.lat.toFixed(6)}, {selectedPlace.geometry.location.lng.toFixed(6)}
                    </span>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${selectedPlace.geometry.location.lat},${selectedPlace.geometry.location.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1"
                    >
                      View on Maps
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {/* Opening Hours */}
              {selectedPlace.opening_hours?.weekday_text && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Opening Hours
                  </h4>
                  <div className="text-sm space-y-1">
                    {selectedPlace.opening_hours.weekday_text.map((text, index) => (
                      <div key={index}>{text}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hierarchy Info */}
              {(selectedPlace.hierarchy_level !== undefined || selectedPlace.nested_places_count) && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Hierarchy Information
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Level:</span> {selectedPlace.hierarchy_level || 0}
                    </div>
                    <div>
                      <span className="text-gray-500">Nested Places:</span> {selectedPlace.nested_places_count || 0}
                    </div>
                    {selectedPlace.floor_level && (
                      <div>
                        <span className="text-gray-500">Floor:</span> {selectedPlace.floor_level}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Photos */}
              {selectedPlace.photos && selectedPlace.photos.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Photos ({selectedPlace.photos.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedPlace.photos.slice(0, 6).map((photo, index) => (
                      <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        {photo.optimized_urls?.thumb ? (
                          <img
                            src={photo.optimized_urls.thumb}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Camera className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};